import type { WordleGameRecord } from './db';
import { LEXICON } from './words';

/**
 * What today's word is, as far as the *household* is concerned.
 *
 * `getDailyTarget` maps the date into the target list, and the claim built on
 * top of it — "both phones compute the same word with no server round-trip"
 * (WORDLE.md §3.3) — holds only while both phones hold the same list. The lists
 * ship inside the bundle. So the moment one phone is a deploy behind, the daily
 * silently stops being daily: two different words on the same date, a duel
 * comparing two different puzzles, and nothing anywhere that says so. Applying
 * deploys automatically (`platform/update.ts`) makes that rare; it cannot make
 * it impossible, because the phone that is behind is exactly the phone that has
 * not noticed anything.
 *
 * So the list stops being the authority. **The first row of the day is.** If
 * somebody in the household has already started today's puzzle, their answer is
 * the answer — it is in a synced row, it is the same row both phones read, and
 * it is already what this player's own board is restored from once they have
 * guessed. Only a day nobody has opened yet falls through to the local list.
 *
 * Which is the right rule on its own terms, not merely a patch: for a *shared*
 * daily, the two of you agreeing matters more than which of the 1,200 words it
 * turned out to be. It also fixes the caveat the lists came with — regenerating
 * them no longer re-maps a date somebody is already playing.
 */
export function householdTarget(
  todayGames: WordleGameRecord[],
  userId: string,
  puzzleDate: string,
): string {
  const live = todayGames.filter((g) => !g.deleted_at && g.puzzle_date === puzzleDate);

  // My own row first — restoring a game must never change its answer mid-puzzle,
  // whatever anyone else did.
  const mine = live.find((g) => g.created_by === userId);
  if (mine) return mine.target_word;

  let first: WordleGameRecord | null = null;
  for (const game of live) if (first === null || startedBefore(game, first)) first = game;
  if (first) return first.target_word;

  return LEXICON.getDailyTarget(puzzleDate);
}

/**
 * Who opened today's puzzle first. `created_at` is the *server's* clock, so it
 * orders two members' rows against one clock rather than two phones'. A row
 * still in the outbox has none yet — but an unstamped row is this phone's own,
 * and its owner has already returned above.
 */
function startedBefore(a: WordleGameRecord, b: WordleGameRecord): boolean {
  const at = a.created_at ?? '';
  const bt = b.created_at ?? '';
  // Never the arrival order: both phones have to reach the same answer, and
  // they pull in whatever order they happen to sync.
  return at === bt ? a.id < b.id : at < bt;
}
